import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import style from "./ChangeEmailModal.module.scss";
import cn from "classnames";
import CustomButton from "../../../custom/custom-button/CustomButton";
import CloseIcon from "../../../shared/icons/CloseIcon";
import CustomInput from "../../../custom/custom-input/CustomInput";
import { User } from "../../../../services";
import { toast } from "react-toastify";
import CheckVerifiedIcon from "../../../shared/icons/CheckVerifiedIcon";
import CircularProgress from "../../../custom/circular-progress/CircularProgress";
import { validateEmail } from "../../../../utils/checkEmail";
import { useUserData } from "../../../../hooks/useUserData";

const ChangeEmailModal = ({ isOpen, onClose, currentEmail }) => {
  const { t } = useTranslation();
  const { refreshUserData } = useUserData();
  const inputs = useRef([]);

  const [email, setEmail] = useState(currentEmail || "");
  const [code, setCode] = useState(["", "", "", ""]);
  const [errorTitleEmail, setErrorTitleEmail] = useState();

  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isOpenCodeModal, setIsOpenCodeModal] = useState(false);
  const [isOpenUpdateEmail, setIsOpenUpdateEmail] = useState(true);
  const [isOpenModalVerified, setIsOpenModalVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUpdateEmail, setIsLoadingUpdateEmail] = useState(false);

  const resetState = () => {
    setEmail(currentEmail || "");
    setCode(["", "", "", ""]);
    setErrorTitleEmail(undefined);
    setIsCodeValid(false);
    setIsOpenCodeModal(false);
    setIsOpenUpdateEmail(true);
    setIsOpenModalVerified(false);
    setIsLoading(false);
    setIsLoadingUpdateEmail(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChange = (value, index) => {
    if (/^\d$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (index < inputs.current.length - 1) {
        inputs.current[index + 1].focus();
      }

      if (newCode.every((digit) => digit !== "")) {
        setIsCodeValid(true);
      } else {
        setIsCodeValid(false);
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (code[index]) {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
        setIsCodeValid(false);
      } else if (index > 0) {
        inputs.current[index - 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 4).split("");

    if (digits.length === 0) return;

    const newCode = ["", "", "", ""];
    digits.forEach((digit, i) => {
      newCode[i] = digit;
    });
    setCode(newCode);

    const isFull = newCode.every((d) => d !== "");
    setIsCodeValid(isFull);

    const focusIndex = Math.min(digits.length, 3);
    if (inputs.current[focusIndex]) {
      inputs.current[focusIndex].focus();
    }
  };

  const handleSendCode = async () => {
    setIsLoading(true);
    try {
      await User.sendConfirmationEmail(email);
      toast.info(t("confirmEmailComponent.toast.codeSent"));
      setIsOpenCodeModal(true);
    } catch (error) {
      console.error("Ошибка при отправке кода:", error);
      toast.error(t("confirmEmailComponent.toast.codeSendError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeEmail = () => {
    setIsLoading(true);

    User.checkCodeEmail(code.join(""), email)
      .then((response) => {
        if (response.data.isSucccess) {
          setIsLoading(false);
          setIsOpenCodeModal(false);
          setIsOpenModalVerified(true);
          updateUserData();
        }
      })
      .catch((error) => {
        console.log("Введен неправильный код подтверждения:", error);
        toast.error(t("confirmEmailComponent.toast.invalidCode"));
        setIsLoading(false);
      });
  };

  const updateUserEmail = async () => {
    setIsLoadingUpdateEmail(true);
    setErrorTitleEmail(undefined);
    if (!validateEmail(email)) {
      setErrorTitleEmail(t("confirmEmailComponent.validation.invalidEmailFormat"));
      setIsLoadingUpdateEmail(false);
      return;
    }
    try {
      const response = await User.check("email", email);
      if (response.data.isExists) {
        toast.error(t('oldRegistrationForm.validation.emailExists'));
        setErrorTitleEmail(t('oldRegistrationForm.validation.emailExists'));
        setIsLoadingUpdateEmail(false);
        return;
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setIsLoadingUpdateEmail(false);
      return;
    }
    User.updateEmail(email)
      .then(() => {
        console.log("email успешно обновлен");
        toast.success(t("confirmEmailComponent.toast.emailChangeVerificationSent", { email: email }), {
          autoClose: 6000,
        });
        setIsLoadingUpdateEmail(false);
        setIsOpenUpdateEmail(false);
        setIsOpenCodeModal(true);
      })
      .catch((error) => {
        console.error("Error updating email:", error);
        toast.error(t("confirmEmailComponent.toast.emailUpdateError"));
        setIsLoadingUpdateEmail(false);
      });
  };

  const updateUserData = () => {
    refreshUserData()
      .catch((error) => {
        console.log(error);
      });
  };

  const handleCodeModalClose = () => {
    setIsOpenCodeModal(false);
    handleClose();
  };

  const handleModalVerifiedClose = () => {
    setIsOpenModalVerified(false);
    handleClose();
  };

  const handleModalEmailClose = () => {
    setIsOpenUpdateEmail(false);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {isOpenUpdateEmail && (
        <div className={cn("modal-overlay", style.modal)}>
          <div className={cn(style.modal_content, style.paddingBt)}>
            <div className={style.modal_text}>
              <div className={style.modal_title}>
                {t("confirmEmailComponent.updateEmailModal.title")}
                <CloseIcon fill={"#6D7885"} onClick={handleModalEmailClose} />{" "}
              </div>
              <div className={style.modal_description}>
                {t("confirmEmailComponent.updateEmailModal.description")}
              </div>
            </div>
            <CustomInput
              placeholder={t("confirmEmailComponent.updateEmailModal.emailPlaceholder")}
              labelText={t("confirmEmailComponent.updateEmailModal.emailLabel")}
              inputType="text"
              onChangeValue={setEmail}
              value={email}
              errorText={errorTitleEmail}
              isDeleteText
            />
            <div className={style.modal_btns}>
              <CustomButton
                text={t("confirmEmailComponent.buttons.getCode")}
                onClick={updateUserEmail}
                isLoading={isLoadingUpdateEmail}
                isFall
              />
            </div>
          </div>
        </div>
      )}

      {isOpenCodeModal && (
        <div className={cn("modal-overlay", style.modal)}>
          <div className={cn(style.modal_content)}>
            <div className={style.modal_text}>
              <div className={style.modal_title}>
                {t("confirmEmailComponent.codeModal.title")}
                <CloseIcon fill={"#6D7885"} onClick={handleCodeModalClose} />{" "}
              </div>
              <div className={style.modal_description}>
                {t("confirmEmailComponent.codeModal.description", { email: email })}
              </div>
            </div>

            <div className={style.codeInputs}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputs.current[index] = el)}
                  value={digit}
                  type="text"
                  maxLength={1}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={style.codeInput}
                  placeholder="0"
                />
              ))}
              <div style={{ display: code.every((digit) => digit === "") ? "none" : "block" }}>
                <CircularProgress
                  size={24}
                  strokeWidth={2}
                  stages={4}
                  currentStage={code.filter((digit) => digit).length}
                  color="#2857FC"
                />
              </div>
            </div>

            <div className={style.modal_btns}>
              <CustomButton
                isFall
                isDisabled={!isCodeValid}
                text={t("confirmEmailComponent.buttons.confirm")}
                onClick={handleCodeEmail}
                isLoading={isLoading}
              />
              <CustomButton
                text={t("confirmEmailComponent.buttons.resendCode")}
                isUnderline
                onClick={handleSendCode}
              />
            </div>
          </div>
        </div>
      )}

      {isOpenModalVerified && (
        <div className={cn("modal-overlay", style.modal)}>
          <div className={cn(style.modal_content)}>
            <div className={style.modal_text}>
              <div className={style.modal_title}>
                {t("confirmEmailComponent.verifiedModal.title")}
                <CloseIcon fill={"#6D7885"} onClick={handleModalVerifiedClose} />{" "}
              </div>
            </div>
            <div className={cn(style.modal_btns, style.modal_checkedText)}>
              <CheckVerifiedIcon />
              {t("confirmEmailComponent.verifiedModal.text")}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChangeEmailModal;
